// HR mega-menu structure, shown in the topbar by HrMegaMenu.
//
// IMPORTANT: HR routes use HrLayout, which renders THIS file. They do NOT use the
// database-driven ONELayout sidebar that other modules use. So enabling a menu in the
// Menu / SubMenu tables has no effect here — it must be added below as well.
//
// Every `to` below was verified against the registered routes in modules/hr/router.
// Paths that do not resolve are deliberately absent rather than shown and broken:
//   - /hr/selfservice/*        no route group exists
//   - /hr/integration/*        no route group exists
//   - /hr/leavemanagement/policy, /hr/reports/employee, /hr/shift/coverage
//   - /hr/contractworkers/invoices  (wrong prefix; real one is contractworkermanagement)
//
// `color` is the per-item accent (icon tint, hover bg, active underline).

export interface HrMenuChild {
  label: string
  description?: string
  icon: string                // primeicons class, e.g. 'pi pi-users'
  to: string                  // router path
  color?: string              // optional override; falls back to parent color
  // Visible to a plain employee. Everything without this is HR/admin only.
  // The menu is an allow-list: adding a screen here is a deliberate decision.
  employee?: boolean
  // Feature the tenant's plan must grant for this item to appear. Omitted means
  // ungated. A child only needs its own code when it differs from its group's -
  // the Lifecycle group, for instance, holds both onboarding and exit screens.
  // See featureCode on HrMenuItem for what this is and is not.
  featureCode?: string
}

export interface HrMenuItem {
  key: string
  label: string
  icon: string
  color: string               // accent (hex), e.g. '#2563eb'
  to?: string
  panelWidth?: number         // px width of the mega-panel
  panelCols?: 1 | 2 | 3
  children?: HrMenuChild[]
  align?: 'left' | 'right'
  // Top-level entry an employee may see (used for link-only groups such as the
  // dashboard). Groups with children are shown when any child is employee-visible.
  employee?: boolean
  // Restricted to actual administrators. The ordinary privileged check covers
  // HR staff and department heads too, which is right for operational screens
  // but too wide for whole-company salary: a department head has no business
  // seeing every employee's pay. Only set this where the data warrants it.
  adminOnly?: boolean
  // Feature the tenant's plan must grant, matching FeatureMaster.Code in the
  // database. A group is shown when its own code is granted OR any of its
  // children's are, so a group spanning two features does not disappear because
  // only one of them is paid for.
  //
  // THIS IS PRESENTATION, NOT ACCESS CONTROL. Hiding a menu entry stops nobody
  // from typing the URL, and anything in the browser can be edited. The real
  // controls are server-side: the licence-filtered menu endpoint returns only
  // granted rows, and the write-gate answers 402 for a lapsed tenant whatever
  // the client believes.
  featureCode?: string
}

export const hrMenu: HrMenuItem[] = [
  {
    key: 'dashboard',
    featureCode: 'CORE',
    employee: true,
    label: 'HR Dashboard',
    icon: 'pi pi-th-large',
    color: '#0e7c8a',
    to: '/hr/dashboard',
  },
  {
    key: 'employees',
    featureCode: 'HR_EMPLOYEE',
    label: 'Employees',
    icon: 'pi pi-users',
    color: '#2563eb',
    panelWidth: 520,
    panelCols: 1,
    children: [
      { label: 'Employee List',      description: 'All employee records',        icon: 'pi pi-list',      to: '/hr/employeemanagement/list', employee: true },
      { label: 'Add Employee',       description: 'Create a new employee',       icon: 'pi pi-user-plus', to: '/hr/employeemanagement/create' },
      // 'Employee Documents' was hidden from this menu on request. Its route
      // /hr/employeemanagement/documents still exists -- restore the entry here to
      // surface it again.
    ],
  },
  {
    // Manpower sits next to Employees because it is about the shape of the
    // workforce rather than any one person: what each ward must hold, what it
    // actually fields, and the requisitions that close the difference.
    key: 'management',
    featureCode: 'HR_PAYROLL',
    label: 'Management',
    icon: 'pi pi-briefcase',
    color: '#126373',
    panelWidth: 560,
    panelCols: 1,
    adminOnly: true,
    children: [
      { label: 'Executive Summary',  description: 'Cost, headcount and what needs signing', icon: 'pi pi-th-large',    to: '/hr/management/overview' },
      { label: 'Salary Comparison',  description: 'Two periods, by department',             icon: 'pi pi-chart-line',  to: '/hr/management/salary-comparison' },
      { label: 'Increment Round',    description: 'Proposed rises against budget',          icon: 'pi pi-arrow-up-right', to: '/hr/management/increments' },
      { label: 'Cost Structure',     description: 'Spread, bands and concentration',        icon: 'pi pi-sitemap',     to: '/hr/management/cost-structure' },
    ],
  },
  {
    key: 'manpower',
    featureCode: 'HR_MANPOWER',
    label: 'Manpower',
    icon: 'pi pi-chart-bar',
    color: '#ea580c',
    panelWidth: 560,
    panelCols: 1,
    children: [
      // Marked `employee` because it is scoped to the signed-in person: it
      // shows only what is assigned to them, so it is safe for anyone who can
      // be named an approver, not just HR.
      { label: 'Waiting on you',   description: 'Approvals assigned to you',       icon: 'pi pi-inbox',     to: '/hr/manpower/my-approvals', employee: true },
      { label: 'Fulfilment Board', description: 'Required vs effective strength',  icon: 'pi pi-chart-bar', to: '/hr/manpower/fulfilment' },
      { label: 'Shortfall Alerts', description: 'Wards below their minimum',       icon: 'pi pi-bell',      to: '/hr/manpower/alerts' },
      { label: 'Requisitions',     description: 'Staffing requests & approvals',   icon: 'pi pi-file-edit', to: '/hr/manpower/requisitions' },
      { label: 'Manpower Norms',   description: 'Minimum strength per ward',       icon: 'pi pi-sliders-h', to: '/hr/manpower/norms' },
    ],
  },
  {
    key: 'attendance',
    featureCode: 'HR_ATTENDANCE',
    label: 'Attendance',
    icon: 'pi pi-calendar',
    color: '#ca8a04',
    panelWidth: 620,
    panelCols: 2,
    children: [
      { label: 'Monthly Attendance',    description: 'Month register',            icon: 'pi pi-calendar',      to: '/hr/attendancemanagement/master' },
      { label: 'Daily Attendance',      description: 'Day-wise attendance',       icon: 'pi pi-clock',         to: '/hr/attendancemanagement/dailyattendance', employee: true },
      { label: 'Attendance Dashboard',  description: 'Today at a glance',         icon: 'pi pi-th-large',      to: '/hr/attendancemanagement/dashboard' },
      { label: 'Regularisation',        description: 'Exceptions & corrections',  icon: 'pi pi-pencil',        to: '/hr/attendancemanagement/exceptions' },
      { label: 'Time Entry',            description: 'Manual time entries',       icon: 'pi pi-stopwatch',     to: '/hr/attendancemanagement/timeentry' },
      { label: 'Overtime Tracking',     description: 'Overtime hours',            icon: 'pi pi-forward',       to: '/hr/attendancemanagement/overtime' },
      { label: 'Shift Handover',        description: 'Handover notes',            icon: 'pi pi-sync',          to: '/hr/attendancemanagement/shift-handover' },
      { label: 'Attendance Analytics',  description: 'Trends & patterns',         icon: 'pi pi-chart-line',    to: '/hr/attendancemanagement/analytics' },
    ],
  },
  {
    key: 'leave',
    featureCode: 'HR_LEAVE',
    label: 'Leave',
    icon: 'pi pi-calendar-times',
    color: '#0891b2',
    panelWidth: 520,
    panelCols: 1,
    children: [
      { label: 'Leave Applications', description: 'Apply and track leave',   icon: 'pi pi-file-edit',   to: '/hr/leavemanagement/applications', employee: true },
      { label: 'Leave Balance',      description: 'Entitlement & balance',   icon: 'pi pi-wallet',      to: '/hr/leavemanagement/balance', employee: true },
      { label: 'Leave Calendar',     description: 'Who is away, and when',   icon: 'pi pi-calendar',    to: '/hr/leavemanagement/calendar', employee: true },
      // The Leave Policy screens existed but were never routed or linked, so nothing in
      // the product could reach them.
      { label: 'Leave Policy',       description: 'Entitlement & accrual rules', icon: 'pi pi-book',   to: '/hr/leavemanagement/policy' },
    ],
  },
  {
    key: 'shift',
    featureCode: 'HR_SHIFT',
    label: 'Shifts',
    icon: 'pi pi-sync',
    color: '#7c3aed',
    panelWidth: 520,
    panelCols: 1,
    children: [
      { label: 'Shift Schedule',     description: 'Roster by ward and date', icon: 'pi pi-calendar',    to: '/hr/shift-management/schedule', employee: true },
      { label: 'Shift Rotation',     description: 'Rotation calendar',       icon: 'pi pi-sync',        to: '/hr/shift-management/rotation' },
      { label: 'Swap Requests',      description: 'Shift swaps to approve',  icon: 'pi pi-arrows-h',    to: '/hr/shift-management/swap-requests' },
      { label: 'Shift Notifications',description: 'Roster alerts',           icon: 'pi pi-bell',        to: '/hr/shift/notifications' },
    ],
  },
  {
    key: 'payroll',
    featureCode: 'HR_PAYROLL',
    label: 'Payroll',
    icon: 'pi pi-wallet',
    color: '#dc2626',
    panelWidth: 620,
    panelCols: 2,
    children: [
      { label: 'Payroll Processor',  description: 'Run payroll for a period',        icon: 'pi pi-play',         to: '/hr/payrollmanagement/process' },
      { label: 'Payroll Periods',    description: 'Create & manage pay months',      icon: 'pi pi-calendar',     to: '/hr/payrollmanagement/periods' },
      { label: 'Approve & Publish',  description: 'Approve, issue payslips, revert',  icon: 'pi pi-verified',     to: '/hr/payrollmanagement/periods-revert' },
      { label: 'Revert Requests',    description: 'Pull an approved payroll back',    icon: 'pi pi-replay',       to: '/hr/payrollmanagement/revert-requests' },
      { label: 'Salary Structures',  description: 'Define & assign structures',      icon: 'pi pi-sitemap',      to: '/hr/payrollmanagement/salary-structures' },
      { label: 'CTC Calculator',     description: 'Work a structure back from a CTC', icon: 'pi pi-calculator',  to: '/hr/payrollmanagement/ctc-calculator' },
      // 'Adjustments', 'Deductions', 'Payroll Reports' and 'Payroll Tax' were
      // hidden from this menu on request. Their routes still exist under
      // /hr/payrollmanagement/*, so restore the entries here to surface them again.
      { label: 'Statutory Reports',  description: 'PT / LWF / Factories / PF / ESI', icon: 'pi pi-shield',       to: '/hr/payrollmanagement/statutory-reports' },
    ],
  },
  {
    key: 'lifecycle',
    featureCode: 'HR_ONBOARDING',
    label: 'Lifecycle',
    icon: 'pi pi-directions',
    color: '#16a34a',
    panelWidth: 640,
    panelCols: 2,
    children: [
      { label: 'Onboarding Programs',  description: 'Reusable programmes',      icon: 'pi pi-flag',        to: '/hr/onboardingmanagement/programs',   color: '#16a34a' },
      { label: 'Onboarding Instances', description: 'Programmes per new hire',  icon: 'pi pi-users',       to: '/hr/onboardingmanagement/instances',  color: '#16a34a' },
      { label: 'Onboarding Tasks',     description: 'Tasks and owners',         icon: 'pi pi-check-square',to: '/hr/onboardingmanagement/tasks',      color: '#16a34a' },
      { label: 'Onboarding Documents', description: 'Documents to collect',     icon: 'pi pi-file',        to: '/hr/onboardingmanagement/documents',  color: '#16a34a' },
      { label: 'Joinee Documents',     description: 'Checklist per designation', icon: 'pi pi-check-square',to: '/hr/onboardingmanagement/verification/documents', color: '#16a34a' },
      { label: 'Background Verification', description: 'Cases, checks & decision', icon: 'pi pi-shield',    to: '/hr/onboardingmanagement/verification', color: '#16a34a' },
      { label: 'Exit Requests',        description: 'Resignations & exits',     icon: 'pi pi-sign-out',    to: '/hr/exitmanagement/requests',         color: '#dc2626', featureCode: 'HR_EXIT' },
      { label: 'Exit Tasks',           description: 'Clearance checklist',      icon: 'pi pi-list-check',  to: '/hr/exitmanagement/tasks',            color: '#dc2626', featureCode: 'HR_EXIT' },
      { label: 'Exit Interviews',      description: 'Interview records',        icon: 'pi pi-comments',    to: '/hr/exitmanagement/interviews',       color: '#dc2626', featureCode: 'HR_EXIT' },
      { label: 'Final Settlements',    description: 'Full & final settlement',  icon: 'pi pi-money-bill',  to: '/hr/exitmanagement/settlements',      color: '#dc2626', featureCode: 'HR_EXIT' },
    ],
  },
  {
    key: 'growth',
    featureCode: 'HR_PERFORMANCE',
    label: 'Performance',
    icon: 'pi pi-chart-line',
    color: '#9333ea',
    panelWidth: 640,
    panelCols: 2,
    children: [
      { label: 'Appraisal Cycles',    description: 'Open, moderate and close', icon: 'pi pi-calendar-plus',to: '/hr/performancemanagement/appraisal' },
      { label: 'My Appraisal',        description: 'Goals and self appraisal', icon: 'pi pi-user-edit',   to: '/hr/performancemanagement/my-appraisal', employee: true },
      { label: 'Appraisals',          description: 'Reviews routed to you',    icon: 'pi pi-inbox',       to: '/hr/performancemanagement/appraisal-inbox' },
      { label: 'Performance Reviews', description: 'Review cycles',            icon: 'pi pi-star',        to: '/hr/performancemanagement/reviews/dashboard' },
      { label: 'Key Result Areas',    description: 'What each role is judged on', icon: 'pi pi-list-check', to: '/hr/performancemanagement/kra-library' },
      { label: 'Goal Setting',        description: 'Goals and tracking',       icon: 'pi pi-flag',        to: '/hr/performancemanagement/goals-tracker' },
      { label: 'Metrics Dashboard',   description: 'Performance metrics',      icon: 'pi pi-chart-bar',   to: '/hr/performancemanagement/metrics/dashboard' },
      { label: 'Development Plans',   description: 'Improvement plans',        icon: 'pi pi-directions',  to: '/hr/performancemanagement/improvement-plans' },
      { label: 'Training Programs',   description: 'Programme catalogue',      icon: 'pi pi-book',        to: '/hr/trainingmanagement/programs', featureCode: 'HR_TRAINING' },
      { label: 'Training Sessions',   description: 'Scheduled runs & rosters', icon: 'pi pi-calendar-plus',to: '/hr/trainingmanagement/sessions', featureCode: 'HR_TRAINING' },
      { label: 'Training Calendar',   description: 'Scheduled training',       icon: 'pi pi-calendar',    to: '/hr/trainingmanagement/development-plans', featureCode: 'HR_TRAINING' },
      { label: 'Assessments',         description: 'Tests and results',        icon: 'pi pi-check-circle',to: '/hr/trainingmanagement/assessments', featureCode: 'HR_TRAINING' },
      { label: 'Certifications',      description: 'Certification currency',   icon: 'pi pi-verified',    to: '/hr/trainingmanagement/certification-tracker', featureCode: 'HR_TRAINING' },
    ],
  },
  {
    key: 'contract',
    featureCode: 'HR_CONTRACT',
    label: 'Contract Staff',
    icon: 'pi pi-briefcase',
    color: '#0f766e',
    panelWidth: 520,
    panelCols: 1,
    children: [
      { label: 'Contract Workers',   description: 'Agency & contract staff', icon: 'pi pi-users',      to: '/hr/contractworkermanagement/workers' },
      { label: 'Contracts',          description: 'Contract register',       icon: 'pi pi-file',       to: '/hr/contractworkermanagement/contracts' },
      { label: 'Contract Dashboard', description: 'Performance & spend',     icon: 'pi pi-chart-bar',  to: '/hr/contractworkermanagement/dashboard' },
    ],
  },
  {
    key: 'masters',
    featureCode: 'MASTERS',
    label: 'Masters',
    icon: 'pi pi-database',
    color: '#0891b2',
    panelWidth: 640,
    panelCols: 2,
    children: [
      { label: 'Designation Master',       description: 'Designations',                  icon: 'pi pi-id-card',        to: '/hr/mastertables/designation' },
      { label: 'Grade Master',             description: 'Pay grades',                    icon: 'pi pi-sort-amount-up', to: '/hr/mastertables/grade' },
      { label: 'Employment Type Master',   description: 'Permanent / contract / intern', icon: 'pi pi-briefcase',      to: '/hr/mastertables/employmenttype' },
      { label: 'Department Master',        description: 'Departments',                   icon: 'pi pi-sitemap',        to: '/hr/mastertables/department' },
      { label: 'Leave Type Master',        description: 'Leave types & rules',           icon: 'pi pi-calendar-times', to: '/hr/mastertables/leavetype' },
      { label: 'Holiday Master',           description: 'Holiday calendar',              icon: 'pi pi-calendar',       to: '/hr/mastertables/holiday', employee: true },
      { label: 'Shift Pattern Master',     description: 'Shift patterns',                icon: 'pi pi-sync',           to: '/hr/mastertables/shiftpattern' },
      { label: 'Exit Type Master',         description: 'Exit types',                    icon: 'pi pi-sign-out',       to: '/hr/mastertables/exittype' },
      { label: 'Onboarding Templates',     description: 'Programme templates',           icon: 'pi pi-copy',           to: '/hr/mastertables/onboardingtemplatemaster' },
      { label: 'Onboarding Task Template', description: 'Task templates',                icon: 'pi pi-check-square',   to: '/hr/mastertables/onboardingtasktemplate' },
    ],
  },
  // The whole 'Reports' group (Attendance / Leave / Compliance reports plus the
  // two Analytics screens) was hidden from this menu on request. The routes
  // still exist under /hr/reports/* and /hr/analytics/*, so restoring the group
  // here is all that is needed to surface it again.
  // {
  //   key: 'reports',
  //   label: 'Reports',
  //   icon: 'pi pi-chart-bar',
  //   color: '#b45309',
  //   panelWidth: 520,
  //   panelCols: 1,
  //   children: [
  //     { label: 'Attendance Reports',   description: 'Attendance registers',   icon: 'pi pi-calendar',    to: '/hr/reports/attendance' },
  //     { label: 'Leave Reports',        description: 'Leave summaries',        icon: 'pi pi-file',        to: '/hr/reports/leave' },
  //     { label: 'Compliance Reports',   description: 'Statutory compliance',   icon: 'pi pi-shield',      to: '/hr/reports/compliance' },
  //     { label: 'Attendance Analytics', description: 'Attendance trends',      icon: 'pi pi-chart-line',  to: '/hr/analytics/attendance' },
  //     { label: 'Performance Analytics',description: 'Performance trends',     icon: 'pi pi-chart-bar',   to: '/hr/analytics/performance' },
  //   ],
  // },
  {
    key: 'admin',
    featureCode: 'SETTINGS',
    label: 'Admin',
    icon: 'pi pi-cog',
    color: '#475569',
    panelWidth: 640,
    panelCols: 2,
    children: [
      { label: 'Masters',             description: 'Department, Sub-Department, Branch, Org', icon: 'pi pi-database',     to: '/hr/user-management/masters',             color: '#2563eb' },
      { label: 'Organization Config', description: 'Org settings & integrations',             icon: 'pi pi-sliders-h',    to: '/hr/user-management/organisation-config', color: '#0891b2' },
      // 'Location Master', 'Location Type' and 'Rule Master' were hidden from this
      // menu on request. Their routes still exist under
      // /hr/user-management/masters/* -- restore the entries here to surface them.
      { label: 'Code Template',       description: 'Auto-code generator templates',           icon: 'pi pi-hashtag',      to: '/hr/user-management/code-template',       color: '#ca8a04' },
      { label: 'Approval',            description: 'Approval matrix',                         icon: 'pi pi-check-square', to: '/hr/user-management/approval',            color: '#7c3aed' },
      { label: 'News & Events',       description: 'Announcements & alerts',                  icon: 'pi pi-megaphone',    to: '/hr/user-management/news-events',         color: '#dc2626' },
      // 'General Settings' -> /hr/settings/general and 'Notifications' ->
      // /hr/settings/notifications were removed: there is no src/modules/hr/settings
      // module, no route and no component behind either, so both links landed on an
      // empty page. Restore them alongside a real settings module.
    ],
  },
]
